import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import ComplianceCalendarForm from "@/components/compliance-ai/ComplianceCalendarForm";
import ComplianceCalendarResponse from "@/components/compliance-ai/ComplianceCalendarResponse";
import { ComplianceCalendarFormData } from "@/types/compliance";
import { API_CONFIG } from "@/constants/env.ts";
import { Calendar, FileText, AlertTriangle, Wrench } from "lucide-react";
import { mockComplianceCalendarResponse } from "@/mocks/complianceCalendarResponse";
import { Button } from "@/components/ui/button";

// Configuration constants
const MAX_COUNTDOWN_SECONDS = 90; // Countdown timer duration in seconds
const MIN_LOADING_TIME_SECONDS = 3; // Minimum time to show loading animation (seconds)

// Marketing messages for the waiting period
const WAIT_TIME_BENEFITS = [
  "Our AI consultant is conducting comprehensive EU regulatory analysis",
  "Creating your personalized compliance roadmap",
  "Identifying industry-specific requirements just for you",
  "AI is scanning 100+ EU directives and regulations"
];

// Component to positively frame the waiting time
const WaitTimeMessage = ({ seconds }: { seconds: number }) => {
  return (
    <div className="mb-8 max-w-xl mx-auto rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 border border-blue-100 dark:border-blue-800 shadow-sm">
      <div className="flex items-start">
        <div className="shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-300">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
            <path d="M12 6v6l4 2"/>
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
            Enhanced AI Analysis in Progress
          </h3>
          <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
            <p>
              Your complete compliance calendar will be ready in approximately {Math.ceil(seconds / 60)} {seconds <= 60 ? 'minute' : 'minutes'}. 
              This allows our AI consultant to perform:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-blue-600 dark:text-blue-400">
              {WAIT_TIME_BENEFITS.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading animation component with countdown
const LoadingAnimation = ({ timeRemaining, setTimeRemaining }: { timeRemaining: number, setTimeRemaining: React.Dispatch<React.SetStateAction<number>> }) => {
  // Start the countdown timer
  useEffect(() => {
    if (timeRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeRemaining, setTimeRemaining]);
  
  // Calculate progress percentage
  const progressPercentage = ((MAX_COUNTDOWN_SECONDS - timeRemaining) / MAX_COUNTDOWN_SECONDS) * 100;
  
  return (
    <div className="min-h-[500px] flex flex-col items-center justify-center">
      {/* Benefit-oriented wait time message */}
      <WaitTimeMessage seconds={MAX_COUNTDOWN_SECONDS} />
      
      <div className="mb-8 flex flex-col items-center">
        <div className="relative w-40 h-40 mb-8">
          {/* Circular progress background */}
          <div className="absolute inset-0 rounded-full border-8 border-gray-200 dark:border-gray-800"></div>
          
          {/* Circular progress fill */}
          <svg className="absolute inset-0 w-full h-full rotate-[-90deg]" viewBox="0 0 100 100">
            <circle 
              cx="50" 
              cy="50" 
              r="46" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="8"
              strokeDasharray="289.27"
              strokeDashoffset={289.27 - (289.27 * progressPercentage / 100)}
              className="text-blue-500 dark:text-blue-400"
            />
          </svg>
          
          {/* Inner pulsing circle */}
          <div className="absolute inset-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {timeRemaining}
            </div>
          </div>
          
          {/* Pulsing ripples */}
          <div className="absolute inset-0 animate-ping opacity-20 rounded-full bg-blue-500 dark:bg-blue-400" style={{ animationDuration: '3s' }}></div>
          <div className="absolute inset-0 animate-ping opacity-20 rounded-full bg-blue-500 dark:bg-blue-400" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
        </div>
        
        <h2 className="text-2xl font-medium text-gray-900 dark:text-gray-100 mb-2">
          Generating Your Compliance Calendar
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
          Our AI consultant is analyzing regulations relevant to your organization
        </p>
      </div>
      
      {/* Animation for the processing stages */}
      <div className="w-full max-w-md">
        <div className="space-y-6">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 0 ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 0 ? '✓' : '1'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Identifying Applicable Regulations</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Assessing EU directives based on your profile</div>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 33 ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 33 ? '✓' : '2'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Analyzing Compliance Requirements</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Evaluating regulatory implications</div>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 66 ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 66 ? '✓' : '3'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Creating Compliance Timeline</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Organizing deadlines and requirements</div>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage >= 100 ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage >= 100 ? '✓' : '4'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Finalizing Calendar</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Preparing your complete compliance calendar</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ComplianceCalendar = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(MAX_COUNTDOWN_SECONDS);
  const [isDevelopment, setIsDevelopment] = useState(false);

  useEffect(() => {
    // Check if we're in development mode
    const hostname = window.location.hostname;
    setIsDevelopment(hostname === "localhost" || hostname === "127.0.0.1");
  }, []);

  const handleSubmit = async (formData: ComplianceCalendarFormData) => {
    setIsLoading(true);
    setError(null);
    setTimeRemaining(MAX_COUNTDOWN_SECONDS);
    
    const minLoadingTime = MIN_LOADING_TIME_SECONDS;
    const startTime = Date.now();
    
    try {
      console.log("Sending request to calendar API:", formData);
      const apiUrl = `${API_CONFIG.API_BASE_URL}/compliance-calendar/`;
      console.log("Full API URL:", apiUrl);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      console.log("API Response:", data);
      const elapsedSeconds = (Date.now() - startTime) / 1000;
      setResponse(data);
      if (elapsedSeconds < minLoadingTime) {
        setTimeout(() => {
          setIsLoading(false);
        }, (minLoadingTime - elapsedSeconds) * 1000);
      } else {
        setIsLoading(false);
      }
      
    } catch (err) {
      console.error('Error submitting form:', err);
      if (err instanceof Error) {
        console.log('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack,
          cause: (err as any).cause
        });
      }
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setIsLoading(false);
    }
  };

  const loadMockResponse = () => {
    setIsLoading(true);
    setError(null);
    setTimeRemaining(MAX_COUNTDOWN_SECONDS);
    
    // Simulate API call with a delay
    setTimeout(() => {
      setResponse(mockComplianceCalendarResponse);
      setIsLoading(false);
    }, MIN_LOADING_TIME_SECONDS * 1000);
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>EU Compliance Calendar Generator | Verdea AI</title>
        <meta 
          name="description" 
          content="Generate a customized compliance calendar for EU directives and regulations relevant to your organization."
        />
      </Helmet>
      
      {/* Background Elements - Apple style with blue theme */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Primary gradient blur */}
        <div
          className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
          style={{
            background: 'radial-gradient(circle at center, rgba(37, 99, 235, 0.3) 0%, rgba(37, 99, 235, 0) 70%)', // Blue-600
            transform: 'translate(20%, -20%)'
          }}
        />

        {/* Secondary gradient blur */}
        <div
          className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(165, 180, 252, 0.3) 0%, rgba(165, 180, 252, 0) 70%)', // Indigo-300
            transform: 'translate(-20%, 20%)'
          }}
        />
        
        {/* Subtle dot grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.015]" 
          style={{
            backgroundImage: 'radial-gradient(rgba(37, 99, 235, 0.2) 1px, transparent 1px)', // Blue-600
            backgroundSize: '40px 40px'
          }}
        />
      </div>
      
      {/* Navbar */}
      <header className="sticky top-0 z-50">
        <NavBar />
      </header>
      
      {/* Hero Section - Apple Style */}
      <section className="pt-36 flex items-center justify-center overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Platform header */}
            <div className="text-center">
              <div>                
                <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
                  EU Compliance
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">
                    Calendar Generator
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed">
                  Get a customized timeline of EU regulatory requirements and upcoming changes based on your organization's profile in 90 seconds or less!
                </p>
              </div>
              
              {/* Dev Mode Button */}
              {isDevelopment && !response && !isLoading && (
                <div className="mb-8">
                  <Button 
                    onClick={loadMockResponse}
                    variant="outline"
                    className="flex items-center gap-2 mx-auto"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Load Mock Response (Dev Only)</span>
                  </Button>
                </div>
              )}
              
              {/* Feature cards */}
              {!response && !isLoading && (
                <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col items-center text-center">
                      <div className="h-12 w-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                        <Calendar className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Customized Timeline</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Get a detailed timeline of applicable EU regulatory deadlines tailored to your organization.</p>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col items-center text-center">
                      <div className="h-12 w-12 rounded-full bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4">
                        <FileText className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Regulatory Requirements</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Understand which EU regulations apply to your specific industry, size, and location.</p>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col items-center text-center">
                      <div className="h-12 w-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                        <AlertTriangle className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Upcoming Changes</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Stay ahead of new EU directives and regulatory changes that will affect your organization.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content Section */}
      <section className="py-16 md:pt-8 md:pb-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            {/* Show form if no response is available */}
            {!response && (
              <div>
                {isLoading ? (
                  <LoadingAnimation 
                    timeRemaining={timeRemaining} 
                    setTimeRemaining={setTimeRemaining} 
                  />
                ) : (
                  <ComplianceCalendarForm 
                    onSubmit={handleSubmit} 
                    isLoading={isLoading} 
                    error={error}
                    waitTimeSeconds={MAX_COUNTDOWN_SECONDS}
                  />
                )}
              </div>
            )}
            
            {/* Show response as soon as it's available, regardless of loading state */}
            {response && (
              <div>
                {isLoading && (
                  <div className="mb-6 flex justify-center">
                    <div className="inline-flex items-center px-4 py-2 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Finalizing results...</span>
                    </div>
                  </div>
                )}
                
                <ComplianceCalendarResponse 
                  response={response} 
                  onReset={() => {
                    setResponse(null);
                    setIsLoading(false);
                  }} 
                />
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default ComplianceCalendar;